// src/GlobalComponents/Header.js
import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { 
  FaBars, FaTimes, FaEnvelope, FaHome, FaUser, FaProjectDiagram, 
  FaServicestack, FaFacebook, FaLinkedin, FaPhone 
} from "react-icons/fa";
import LogoB from "../assets/images/LogoB.png";
import ContactFormModal from "../GlobalComponents/ContactFormModal";

export default function Header({ cartCount = 0 }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const location = useLocation();

  const toggleMobileMenu = () => setMobileMenuOpen(s => !s);
  const toggleContactForm = () => setShowContactForm(prev => !prev);

  const handleLogoClick = () => {
    if (location.pathname !== '/') {
      window.location.href = '/';
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    const checkIfMobile = () => setIsMobile(window.innerWidth <= 880);
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  // Theme colors matching the rest of the site
  const GRADIENT_PRIMARY = 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)';
  const GRADIENT_ACTIVE = 'linear-gradient(135deg, #1e2b38 0%, #2980b9 100%)';

  const filledStyle = {
    background: GRADIENT_PRIMARY,
    color: '#ffffff',
    border: 'none',
    boxShadow: '0 4px 15px rgba(52, 152, 219, 0.3)',
  };
  const activeSoftFilled = {
    background: GRADIENT_ACTIVE,
    color: '#ffffff',
    border: 'none',
    boxShadow: '0 4px 15px rgba(52, 152, 219, 0.2)',
    filter: 'brightness(0.95)',
    transform: 'translateY(0)',
  };

  const styles = {
    headerWrapper: {
      position: 'sticky',
      top: 0,
      zIndex: 120,
      width: '100%',
    },
    topBar: {
      background: GRADIENT_PRIMARY,
      color: '#ffffff',
      padding: '8px 20px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      fontSize: '0.9rem',
      fontFamily: "'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
      borderBottom: '1px solid rgba(255,255,255,0.1)',
    },
    topBarLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: '20px',
    },
    topBarRight: {
      display: 'flex',
      alignItems: 'center',
      gap: '15px',
    },
    contactItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      color: '#ffffff',
      textDecoration: 'none',
      transition: 'color 0.2s ease',
    },
    socialIcon: {
      color: '#ffffff',
      fontSize: '1.1rem',
      transition: 'color 0.2s ease, transform 0.2s ease',
    },
    mainHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 16,
      padding: '10px 20px',
      backdropFilter: 'blur(8px)',
      WebkitBackdropFilter: 'blur(8px)',
      background: 'transparent',
      borderBottom: 'none',
      boxShadow: '0 6px 30px rgba(2,8,23,0.6)',
      alignSelf: 'stretch',
      minHeight: 64,
      fontFamily: "'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
    },
    logoContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      cursor: 'pointer',
    },
    logoImage: {
      height: 44,
      width: 'auto',
      display: 'block',
      // shadow removed as requested
    },
    nav: {
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      flex: '1 1 auto',
      justifyContent: 'center',
    },
    navButton: (isActive) => ({
      textDecoration: 'none',
      padding: '10px 18px',
      borderRadius: 999,
      fontWeight: 600,
      fontSize: 15,
      display: 'inline-flex',
      alignItems: 'center',
      gap: 8,
      transition: 'all 180ms cubic-bezier(.2,.9,.3,1)',
      ...(isActive ? activeSoftFilled : filledStyle),
    }),
    rightControls: {
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      flex: '0 0 auto',
    },
    iconButton: (toggled = false) => ({
      padding: 10,
      borderRadius: 12,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      minWidth: 44,
      minHeight: 44,
      transition: 'transform 160ms ease, box-shadow 160ms ease, filter 160ms ease',
      ...(toggled ? activeSoftFilled : filledStyle),
    }),
    envelopeIcon: {
      fontSize: 18,
      color: '#ffffff',
    },
    cartBadge: {
      position: 'absolute',
      top: -6,
      right: -6,
      background: '#3498db',
      color: '#ffffff',
      borderRadius: 99,
      width: 20,
      height: 20,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 12,
      fontWeight: 700,
      boxShadow: '0 4px 10px rgba(52, 152, 219, 0.3)',
    },
    mobileNav: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
    },
    mobileMenuOverlay: {
      position: 'fixed',
      inset: 0,
      background: 'linear-gradient(180deg, rgba(0,0,0,0.45), rgba(2,6,12,0.6))',
      zIndex: 200,
      display: 'flex',
      justifyContent: 'flex-end',
      transition: 'opacity 180ms ease',
    },
    mobileMenu: {
      width: 300,
      height: '100%',
      background: 'linear-gradient(180deg, rgba(44,62,80,0.95), rgba(52,152,219,0.9))',
      padding: 20,
      boxShadow: '-20px 0 60px rgba(2,8,23,0.6)',
    },
    mobileMenuItem: (isActive) => ({
      display: 'flex',
      gap: 12,
      alignItems: 'center',
      padding: '12px 14px',
      textDecoration: 'none',
      borderRadius: 10,
      fontWeight: 600,
      ...(isActive ? activeSoftFilled : filledStyle),
    }),
    mobileIcon: { fontSize: 18, color: '#ffffff' },
  };

  const links = [
    { to: '/', label: 'Home', icon: <FaHome /> },
    { to: '/about', label: 'About', icon: <FaUser /> },
    { to: '/services', label: 'Services', icon: <FaServicestack /> },
    { to: '/projects', label: 'Projects', icon: <FaProjectDiagram /> },
  ];

  return (
    <>
      <div style={styles.headerWrapper}>
        {/* Top Bar */}
        <div style={styles.topBar} className="top-bar">
          <div style={styles.topBarLeft}>
            <a href="tel:+27113940990" style={styles.contactItem} className="contact-item">
              <FaPhone /> <span>011 394 0990</span>
            </a>
            <a href="mailto:info@zwiko.co.za" style={styles.contactItem} className="contact-item">
              <FaEnvelope /> <span>info@zwiko.co.za</span>
            </a>
          </div>
          <div style={styles.topBarRight}>
            <a 
              href="https://www.facebook.com/mashavhapestcontrol" 
              target="_blank" 
              rel="noopener noreferrer"
              style={styles.socialIcon}
              className="social-icon"
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#3498db';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = '#ffffff';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <FaFacebook />
            </a>
            <a 
              href="https://www.linkedin.com/company/mashavha-pest-control" 
              target="_blank" 
              rel="noopener noreferrer"
              style={styles.socialIcon}
              className="social-icon"
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#3498db';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = '#ffffff';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <FaLinkedin />
            </a>
          </div>
        </div>

        {/* Main Header */}
        <header style={styles.mainHeader} aria-label="Primary header">
          <div style={styles.logoContainer} onClick={handleLogoClick} role="button" tabIndex={0} aria-label="Go to home">
            <img src={LogoB} alt="Logo" style={styles.logoImage} />
          </div>

          {/* Desktop nav */}
          {!isMobile && (
            <nav style={styles.nav} role="navigation" aria-label="Primary navigation">
              {links.map((l) => {
                const isActive = location.pathname === l.to;
                return (
                  <Link key={l.to} to={l.to} style={styles.navButton(isActive)}>
                    {l.icon}
                    <span>{l.label}</span>
                  </Link>
                );
              })}
            </nav>
          )}

          <div style={styles.rightControls}>
            {!isMobile && (
              <button
                type="button"
                onClick={toggleContactForm}
                title="Contact"
                aria-label="Contact"
                style={{ ...styles.iconButton(false), position: 'relative' }}
              >
                <FaEnvelope style={styles.envelopeIcon} />
                {cartCount > 0 && <span style={styles.cartBadge}>{cartCount}</span>}
              </button>
            )}

            {/* Mobile controls */}
            {isMobile && (
              <div style={styles.mobileNav}>
                <button style={{ ...styles.iconButton(false), position: 'relative' }} onClick={toggleContactForm} aria-label="Contact">
                  <FaEnvelope style={styles.envelopeIcon} />
                  {cartCount > 0 && <span style={styles.cartBadge}>{cartCount}</span>}
                </button>

                <button
                  type="button"
                  onClick={toggleMobileMenu}
                  aria-label="Open menu"
                  style={{ ...styles.iconButton(mobileMenuOpen) }}
                >
                  {mobileMenuOpen ? <FaTimes style={{ fontSize: 18, color: '#ffffff' }} /> : <FaBars style={{ fontSize: 18, color: '#ffffff' }} />}
                </button>
              </div>
            )}
          </div>

          {/* Mobile drawer */}
          {mobileMenuOpen && isMobile && (
            <div style={styles.mobileMenuOverlay} onClick={toggleMobileMenu}>
              <div style={styles.mobileMenu} onClick={(e) => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <img src={LogoB} alt="logo" style={{ height: 36 }} />
                  </div>
                  <button onClick={toggleMobileMenu} style={{ background: 'none', border: 'none', color: '#ffffff', fontSize: 20 }} aria-label="Close menu"><FaTimes /></button>
                </div>

                <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {links.map(l => {
                    const isActive = location.pathname === l.to;
                    return (
                      <Link key={l.to} to={l.to} style={styles.mobileMenuItem(isActive)} onClick={() => setMobileMenuOpen(false)}>
                        <span style={styles.mobileIcon}>{l.icon}</span>
                        <span>{l.label}</span>
                      </Link>
                    );
                  })}

                  <button type="button" onClick={() => { toggleContactForm(); setMobileMenuOpen(false); }} style={{ ...styles.mobileMenuItem(false), marginTop: 8 }}>
                    <FaEnvelope style={styles.mobileIcon} /> <span> Contact</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </header>
      </div>

      {/* Contact Form Modal */}
      <ContactFormModal isOpen={showContactForm} onClose={() => setShowContactForm(false)} />

      {/* Responsive styles for top bar */}
      <style>{`
        @media (max-width: 768px) {
          .contact-item span {
            display: none;
          }
          .top-bar {
            padding: 8px 10px;
          }
        }
      `}</style>
    </>
  );
}